import React, { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';
import queryString from 'query-string';
import axios from 'axios';
import ReactGA from 'react-ga';

import {
  createUltimatePagination,
  ITEM_TYPES,
  PaginationComponentProps,
} from 'react-ultimate-pagination';
import { tabResultState } from '../../../modules/tabResult';
import FindURL from '../common/FindURL';

import IconArrow from '../../../assets/icon/ic-back@2x.png';
import { GetSearchParamType } from '../../../lib/tabResult/type';
import config from '../../../lib/config-api.json';

import {
  ItemContainer,
  ItemDescBox,
  ItemSourceBox,
  ItemTitleBox,
  PaginationBox,
  PaginationButton,
  ListContainter,
  MoreViewArrow,
  MoreViewBox,
  MoreViewTitle,
} from '../common/Layout';

import SkeletonCard from '../common/SkeletonCard';
import { PostConsumptionBodyType } from '../../../lib/common/type';

const env = process.env.NODE_ENV;
let { apiInfo } = config;

if (env === 'development') {
  apiInfo = config.apiInfo_dev;
}

const Page = ({
  value,
  isActive,
  onClick,
  isDisabled,
}: PaginationComponentProps) => (
  <PaginationButton
    type="button"
    onClick={onClick}
    disabled={isDisabled}
    isActive={isActive}
  >
    {value}
  </PaginationButton>
);

const Ellipsis = ({ onClick, isDisabled }: PaginationComponentProps) => (
  <PaginationButton type="button" onClick={onClick} disabled={isDisabled}>
    ...
  </PaginationButton>
);

const FirstPageLink = ({ onClick, isDisabled }: PaginationComponentProps) => (
  <PaginationButton type="button" onClick={onClick} disabled={isDisabled}>
    &lt;
  </PaginationButton>
);

const PreviousPageLink = ({
  onClick,
  isDisabled,
}: PaginationComponentProps) => {
  return (
    <PaginationButton type="button" onClick={onClick} disabled={isDisabled}>
      &lt;
    </PaginationButton>
  );
};

const NextPageLink = ({ onClick, isDisabled }: PaginationComponentProps) => (
  <PaginationButton type="button" onClick={onClick} disabled={isDisabled}>
    &gt;
  </PaginationButton>
);

const LastPageLink = ({ onClick, isDisabled }: PaginationComponentProps) => (
  <PaginationButton type="button" onClick={onClick} disabled={isDisabled}>
    &gt;
  </PaginationButton>
);

const itemTypeToComponent = {
  [ITEM_TYPES.PAGE]: Page,
  [ITEM_TYPES.ELLIPSIS]: Ellipsis,
  [ITEM_TYPES.FIRST_PAGE_LINK]: FirstPageLink,
  [ITEM_TYPES.PREVIOUS_PAGE_LINK]: PreviousPageLink,
  [ITEM_TYPES.NEXT_PAGE_LINK]: NextPageLink,
  [ITEM_TYPES.LAST_PAGE_LINK]: LastPageLink,
};

const UltimatePaginationBasic = createUltimatePagination({
  itemTypeToComponent,
});

type SearchListProps = {
  travel_info?: tabResultState['travel_info'];
  recommended_info?: tabResultState['recommended_info'];
  course_info?: tabResultState['course_info'];
  attachResult?: tabResultState['attachResult'];

  loading: boolean;
  onPostConsumption: (info: PostConsumptionBodyType) => void;
  onGetSearch: (info: GetSearchParamType) => void;
  onSetIsMoreInfoResult: (
    isMoreInfoResult: tabResultState['isMoreInfoResult']
  ) => void;
};

type SearchListState = {
  totalPage: number;
  page: number;
};

function isString(val: string | string[]): val is string {
  return typeof val === 'string';
}

class SearchList extends React.Component<SearchListProps, SearchListState> {
  state = {
    totalPage: 10,
    page: 1,
  };

  componentDidMount() {
    const {
      onGetSearch,
      travel_info,
      recommended_info,
      course_info,
      attachResult,
    } = this.props;
    let targets = 'travel_info';
    window.scrollTo(0, 0);

    if (travel_info && travel_info.total) {
      this.findTotalPage(travel_info.total);
      targets = 'travel_info';
    } else if (recommended_info && recommended_info.total) {
      this.findTotalPage(recommended_info.total);
      targets = 'recommended_info';
    } else if (course_info && course_info.total) {
      this.findTotalPage(course_info.total);
      targets = 'course_info';
    } else if (attachResult && attachResult.total) {
      this.findTotalPage(attachResult.total);
    }

    onGetSearch({
      page: 1,
      page_count: 10,
      session: localStorage.getItem('aivory_session_id') || 'test_session',
      targets,
      query: this.findKeyword(),
      type: localStorage.getItem('aivory_option_sort') || '0',
      search_option: localStorage.getItem('aivory_option_result') || '',
      start_date: localStorage.getItem('aivory_option_date_start') || '',
      end_date: localStorage.getItem('aivory_option_date_end') || '',
    });
  }

  shouldComponentUpdate(
    nextProps: SearchListProps,
    nextState: SearchListState
  ) {
    const { travel_info, recommended_info, course_info, attachResult } = this.props;

    if (nextProps.travel_info && travel_info) {
      if (nextProps.travel_info.total !== travel_info.total) {
        if (nextProps.travel_info.total && nextProps.travel_info.total !== 0) {
          this.findTotalPage(nextProps.travel_info.total);
        }
        return true;
      }
    } else if (nextProps.recommended_info && recommended_info) {
      if (nextProps.recommended_info.total !== recommended_info.total) {
        if (
          nextProps.recommended_info.total &&
          nextProps.recommended_info.total !== 0
        ) {
          this.findTotalPage(nextProps.recommended_info.total);
        }
        return true;
      }
    } else if (nextProps.course_info && course_info) {
      if (nextProps.course_info.total !== course_info.total) {
        if (nextProps.course_info.total && nextProps.course_info.total !== 0) {
          this.findTotalPage(nextProps.course_info.total);
        }
        return true;
      }
    } else if (nextProps.attachResult && attachResult) {
      if (nextProps.attachResult.total !== attachResult.total) {
        if (nextProps.attachResult.total && nextProps.attachResult.total !== 0) {
          this.findTotalPage(nextProps.attachResult.total);
        }
        return true;
      }
    }
    

    return true;
  }

  findTotalPage = (total: number) => {
    let totalPage = 0;
    if (total !== 0) {
      if (total > 10000) {
        totalPage = 1000;
      } else {
        totalPage = Math.floor(total / 10);

        if (total % 10 !== 0) {
          totalPage += 1;
        }
      }
      this.setState({
        totalPage,
      });
    }
  };

  findKeyword = () => {
    const query = queryString.parse(window.location.search);
    let keyword: string | string[] = '';

    if (query.q) {
      if (isString(query.q)) {
        keyword = query.q;
      } else {
        const index = 0;
        keyword = query.q[index];
      }
    }
    return keyword;
  };

  onPageChange = (page: number) => {
    const {
      onGetSearch,
      travel_info,
      course_info,
      recommended_info,
      attachResult,
    } = this.props;
    let targets = 'travel_info';
    window.scrollTo(0, 0);
    if (travel_info && travel_info.results) {
      targets = 'travel_info';
    } else if (recommended_info && recommended_info.results) {
      targets = 'recommended_info';
    } else if (course_info && course_info.results) {
      targets = 'course_info';
    } else if (attachResult && attachResult.results) {
      targets = 'attachResult';
    }

    onGetSearch({
      page,
      page_count: 10,
      session: localStorage.getItem('aivory_session_id') || 'test_session',
      targets,
      query: this.findKeyword(),
      type: localStorage.getItem('aivory_option_sort') || '0',
      search_option: localStorage.getItem('aivory_option_result') || '',
      start_date: localStorage.getItem('aivory_option_date_start') || '',
      end_date: localStorage.getItem('aivory_option_date_end') || '',
    });
    this.setState({ page });
  };

  ConsumtionContent = (id: number, targetId: number) => {
    const { onPostConsumption } = this.props;
    let targets = 'travel_info';
    switch (targetId) {
      case 0:
        targets = 'travel_info';
        break;
      case 1:
        targets = 'recommended_info';
        break;
      case 2:
        targets = 'course_info';
        break;
      case 3:
        targets = 'attachResult';
      break;
      default:
        break;
    }

    ReactGA.ga(
      'send',
      'event',
      `click_search_result_view_more_normal_contents`
    );
    const data = {
      primary_key: id,
      target: targets,
      document_type: 'normal',
      session: localStorage.getItem('aivory_session_id') || 'test_session',
      reaction_type: 0,
      read_type: 0,
    };
    const newData = {
      primary_key: id,
      target: targets,
      document_type: 'normal',
      read_type: 0,
    };
    onPostConsumption(newData);
    axios({
      method: 'post',
      url: `${apiInfo.api_url}/read/`,
      timeout: 10000,
      data,
    });
  };

  contentFrame = (
    source: string,
    url: string,
    id: number,
    targetId: number,
    location_depth_1: string,
    location_depth_2: string,
    title?: string,
    content?: string,
    highlightTitle?: string,
    highlightContent?: string
  ): ReactNode | null => {
    const resultReactNode = (
      <ItemContainer>
        {highlightTitle ? (
          <ItemTitleBox
            href={url}
            target="_blank"
            dangerouslySetInnerHTML={{
              __html: highlightTitle,
            }}
            onClick={() => {
              this.ConsumtionContent(id, targetId);
            }}
          />
        ) : (
          <ItemTitleBox href={url} target="_blank">
            {title}
          </ItemTitleBox>
        )}

        {highlightContent ? (
          <ItemDescBox
            dangerouslySetInnerHTML={{
              __html: highlightContent,
            }}
          />
        ) : (
          <ItemDescBox>{content}</ItemDescBox>
        )}

        <ItemSourceBox>
          {`${location_depth_1} `}
          {location_depth_2}
        </ItemSourceBox>
      </ItemContainer>
    );

    return resultReactNode;
  };

  makeList = (): ReactNode[] | null => {
    const { course_info, recommended_info, travel_info, attachResult } = this.props;
    const resultReactNode: ReactNode[] | null = [];
    if (travel_info && travel_info.results) {
      travel_info.results.forEach((element) => {
        const urlInfo = FindURL(element.cot_id, 0);
        resultReactNode.push(
          this.contentFrame(
            urlInfo.source,
            urlInfo.url,
            element.id,
            0,
            element.location_depth_1,
            element.location_depth_2,
            element.title,
            element.content,
            element.highlight.title?.[0] || undefined,
            element.highlight.content?.[0] || undefined
          )
        );
        return false;
      });
    } else if (recommended_info && recommended_info.results) {
      recommended_info.results.forEach((element) => {
        const urlInfo = FindURL(element.cot_id, 1);
        resultReactNode.push(
          this.contentFrame(
            urlInfo.source,
            urlInfo.url,
            element.id,
            1,
            element.location_depth_1,
            element.location_depth_2,
            element.title,
            element.content,
            element.highlight.title?.[0] || undefined,
            element.highlight.content?.[0] || undefined
          )
        );
        return false;
      });
    } else if (course_info && course_info.results) {
      course_info.results.forEach((element) => {
        const urlInfo = FindURL(element.cot_id, 1);
        resultReactNode.push(
          this.contentFrame(
            urlInfo.source,
            urlInfo.url,
            element.id,
            2,
            element.location_depth_1,
            element.location_depth_2,
            element.title,
            element.content,
            element.highlight.title?.[0] || undefined,
            element.highlight.content?.[0] || undefined
          )
        );
        return false;
      });
    } else if (attachResult && attachResult.results) {
      attachResult.results.forEach((element) => {
        resultReactNode.push(
          this.contentFrame(
            '',
            element.download_url,
            0,
            3,
            '',
            '',
            element.name,
            element.normalized_text,
            undefined,
            undefined
          )
        );
        return false;
      });
    }

    return resultReactNode;
  };

  onClickBack = () => {
    const { onSetIsMoreInfoResult } = this.props;
    onSetIsMoreInfoResult(false);
    const { onGetSearch } = this.props;

    onGetSearch({
      page: 1,
      page_count: 5,
      session: localStorage.getItem('aivory_session_id') || 'test_session',
      targets: 'travel_info,recommended_info,course_info',
      query: this.findKeyword(),
      type: localStorage.getItem('aivory_option_sort') || '0',
      search_option: localStorage.getItem('aivory_option_result') || '',
      start_date: localStorage.getItem('aivory_option_date_start') || '',
      end_date: localStorage.getItem('aivory_option_date_end') || '',
    });
  };

  render() {
    const { loading } = this.props;
    const { page, totalPage } = this.state;

    return (
      <ListContainter>
        <MoreViewBox>
          <MoreViewArrow src={IconArrow} alt="more view" />
          <MoreViewTitle
            onClick={() => {
              this.onClickBack();
            }}
          >
            검색결과 페이지
          </MoreViewTitle>
        </MoreViewBox>
        {loading ? <SkeletonCard /> : <>{this.makeList()}</>}

        <PaginationBox>
          <UltimatePaginationBasic
            currentPage={page}
            totalPages={totalPage}
            onChange={this.onPageChange}
            hideFirstAndLastPageLinks
            boundaryPagesRange={0}
            siblingPagesRange={1}
          />
        </PaginationBox>
      </ListContainter>
    );
  }
}

export default SearchList;
